import 'pannellum/build/pannellum.css';
import React, {useEffect, useRef, useState} from 'react';
import {startTransition} from 'react';

export const Pannellum = ({
                              imgShow,
                              width,
                              image,
                              pitch,
                              height,
                              yaw,
                              hfov = 100,
                              autoLoad,
                              event,
                              handleEvent,
                              openImage,
                              autoRotate,
                              data,
                              setZoom,
                              draggable = true,
                              resizingImage,
                              onImageLoaded
                          }) => {
    const panorama = useRef();
    const viewerRef = useRef(null);
    const latestImage = useRef(image);
    const [style, setStyle] = useState({
        height: height,
        width: width,
    });
    const [isZoom, setIsZoom] = useState(false);

    const destroyViewer = () => {
        const viewer = viewerRef.current;
        viewerRef.current = null;
        if (viewer && viewer.destroy) {
            try {
                viewer.destroy();
            } catch (e) {
            }
        }
        if (panorama.current)
            panorama.current.innerHTML = "";
    };

    const start = () => {
        const url = image;
        if (!url) return;
        latestImage.current = url;
        import('pannellum').then(() => {
            if (latestImage.current !== url) return; // superseded by a newer image
            if (!panorama.current) return;
            destroyViewer();
            const viewer = window.pannellum.viewer(panorama.current, {
                autoLoad: autoLoad,
                hfov,
                panorama: url,
                pitch,
                type: "equirectangular",
                yaw,
                autoRotate: autoRotate,
                showControls: false,
                preview: "/static/public/img/pannellum_load_img.jpeg",
                draggable,
                doubleClickZoom: false,
                }).on(event, (e) => {
                    if (onImageLoaded) onImageLoaded(url);
                    if (handleEvent) handleEvent(viewer);
                });
            if (latestImage.current !== url) {
                try {
                    viewer.destroy();
                } catch (e) {
                }
                return;
            }
            viewerRef.current = viewer;
        });
    }

    useEffect(() => {
        if (image)
            start();
        return () => {
            latestImage.current = null;
            destroyViewer();
        };
    }, [image, draggable, resizingImage]);

    // Pannellum cannot size itself while the container is display:none; once it
    // becomes visible (or the splash gating flips) re-initialize if it got a 0-size.
    useEffect(() => {
        if (!image || !panorama.current) return;
        if (panorama.current.clientWidth === 0 || panorama.current.clientHeight === 0)
            start();
    }, [imgShow, openImage]);

    // Sync auto-rotate at runtime so a hidden pano that gets promoted to the
    // displayed one starts spinning (and a demoted one stops).
    useEffect(() => {
        const viewer = viewerRef.current;
        if (!viewer) return;
        try {
            if (autoRotate) {
                viewer.startAutoRotate(autoRotate);
            } else {
                viewer.stopAutoRotate();
            }
        } catch (e) {
        }
    }, [autoRotate]);

    useEffect(() => {
        setStyle({
            ...style,
            display: !openImage ? 'none' : 'block'
        })
    }, [openImage, imgShow]);

    useEffect(() => {
        setStyle({
            ...style,
            height,
        })
    }, [height, imgShow]);

    return (<div className="imageWrap" ref={panorama} style={isZoom ? {
        height: '100vh',
        width: window.matchMedia('(orientation: portrait)').matches ? '150vh' : '100vw',
        position: 'fixed',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 999,
    } : style}>
        {setZoom ? <div className="pannellum-controls"
                        style={{
                            left: (isZoom && window.matchMedia('(orientation: portrait)').matches) ? `calc((150vh - 100vw) / 2)` : 0,
                        }}
                        onClick={() => {
                            setIsZoom(!isZoom);
                            setZoom(prev => !prev);
                            let address = document.querySelector('.addressBlock');
                            if (address)
                                address.style.display = address.style.display === 'block' ? 'none' : 'block';
                        }}>
            {isZoom ? <svg xmlns="http://www.w3.org/2000/svg"
                           width="100%"
                           viewBox="0 0 30 31"
                           fill="none">
                    <path fillRule="evenodd"
                          opacity="0.8"
                          clipRule="evenodd"
                          d="M20.0831 19.0673H21.438L29.9961 27.5587L27.4407 30.0892L18.8655 21.6147V20.2731L18.4024 19.7976C16.4473 21.4619 13.909 22.4639 11.1478 22.4639C4.99077 22.4639 0 17.5219 0 11.4251C0 5.32823 4.99077 0.38623 11.1478 0.38623C17.3048 0.38623 22.2956 5.32823 22.2956 11.4251C22.2956 14.1593 21.2837 16.6727 19.6029 18.6088L20.0831 19.0673ZM3.43008 11.4251C3.43008 15.6538 6.87732 19.0673 11.1478 19.0673C15.4182 19.0673 18.8655 15.6538 18.8655 11.4251C18.8655 7.19635 15.4182 3.7828 11.1478 3.7828C6.87732 3.7828 3.43008 7.19635 3.43008 11.4251Z"
                          fill="#ffffff"/>
                    <path d="M15.5979 12.5052H12.2383H10.5585H7.19897V10.8418H10.5585H12.2383H15.5979V12.5052Z"
                          opacity="0.8"
                          fill="#ffffff"/>
                </svg> :
                <svg xmlns="http://www.w3.org/2000/svg"
                     width="100%"
                     viewBox="0 0 50 50"
                     fill="none">
                    <g opacity="0.8">
                        <path fillRule="evenodd"
                              clipRule="evenodd"
                              d="M33.4763 31.4465H35.7347L50 45.7404L45.7404 50L31.4465 35.7347V33.4763L30.6747 32.6758C27.4157 35.4774 23.1847 37.1641 18.582 37.1641C8.31904 37.1641 0 28.8451 0 18.582C0 8.31904 8.31904 0 18.582 0C28.8451 0 37.1641 8.31904 37.1641 18.582C37.1641 23.1847 35.4774 27.4157 32.6758 30.6747L33.4763 31.4465ZM5.71756 18.582C5.71756 25.7004 11.4637 31.4465 18.582 31.4465C25.7004 31.4465 31.4465 25.7004 31.4465 18.582C31.4465 11.4637 25.7004 5.71755 18.582 5.71755C11.4637 5.71755 5.71756 11.4637 5.71756 18.582Z"
                              fill="#ffffff"/>
                        <path d="M26 20.4H20.4V26H17.6V20.4H12V17.6H17.6V12H20.4V17.6H26V20.4Z" fill="#ffffff"/>
                    </g>
                </svg>}
        </div> : ''}
    </div>);
};
